import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

/** GET /admin/claims/by-number/{n}/financial-health — FamilyPaymentBreakdown / FinancialHealth (Vue). */
export function useClaimFinancialHealth(claimNumber: number | undefined) {
  return useQuery({
    queryKey: [...queryKeys.payments.all, "financial-health", claimNumber],
    queryFn: () => adminAPI.getClaimFinancialHealth(claimNumber as number),
    enabled: claimNumber != null,
  });
}

type CounterpartyStatus = Parameters<typeof adminAPI.updatePaymentCounterparty>[2]["status"];

/** Mirrors FamilyPaymentBreakdown.updateStatus (Vue). */
export function useUpdatePaymentCounterparty(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ counterpartyId, status }: { counterpartyId: string; status: CounterpartyStatus }) =>
      notify.promise(adminAPI.updatePaymentCounterparty(claimId, counterpartyId, { status }), {
        loading: "Mise à jour du statut…",
        success: "Statut mis à jour",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.payments.all });
    },
  });
}

/** Mirrors TravelerPayments.generateReceipt / regenerateReceipt (Vue). */
export function useGeneratePaymentReceipt(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      familyClaimAcquisitionId,
      force,
    }: {
      familyClaimAcquisitionId: string;
      force?: boolean;
    }) =>
      notify.promise(
        adminAPI.generatePaymentReceipt(claimId, familyClaimAcquisitionId, force ? { force } : undefined),
        {
          loading: force ? "Re-génération de la quittance…" : "Génération de la quittance…",
          success: force ? "Quittance re-générée" : "Quittance générée",
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.payments.all });
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

/** Mirrors TravelerPayments.sendReceiptEmail (Vue). */
export function useSendPaymentReceipt(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (familyClaimAcquisitionId: string) =>
      notify.promise(adminAPI.sendPaymentReceipt(claimId, familyClaimAcquisitionId), {
        loading: "Envoi de la quittance…",
        success: "Email de quittance envoyé",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.payments.all });
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}
